// Plano de ação — /administrativo/plano-acao
//
// Um plano com semanas, tarefas, informações, decisões e indicadores (primeiro
// uso: Ciclo 1 com a Bcorp). Quem vê/edita/gere: `gestao.plano_acao.*`, conferido
// no servidor; aqui só se esconde botão (regras.ts). O andamento mora na própria
// tarefa (nada vira atividade); cada tarefa tem página própria (PaginaTarefa).
// Contrato das ações: docs/plano-acao.md.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, App as AntApp, Button, Card, Empty, Form, Modal, Progress, Select, Skeleton, Space, Tabs, Typography } from 'antd';
import {
  atualizarDoModelo, ehConflito, ehSemAcesso, erroDe, importarModelo, ligarCiclo, listarCiclos, listarContas, obterCiclo,
  type CicloCompleto, type CicloResumo, type Conta, type ItemPrevia, type PreviaModelo,
} from './api';
import { AbaAntesEMarcos, AbaIndicadores, AbaPessoas } from './AbasSimples';
import AbaSemanas from './AbaSemanas';
import { CampoData, type Contexto } from './comum';
import { fmtDataBr } from '../../utils/dataFmt';
import { fraseDaPrevia, fraseDaSemana, hojeIso, resumoDoCiclo, segundaDaSemana, semanaAtual } from './regras';

const { Title, Text } = Typography;

const textoDoItem = (i: ItemPrevia): string => fraseDaPrevia(i);

const MODELOS = [{ value: 'bcorp-ciclo1', label: 'Bcorp — Ciclo 1: previsibilidade na geração de demanda' }];

export default function PlanoDeAcao() {
  const { message, modal } = AntApp.useApp();
  const hoje = hojeIso();
  const [busca] = useSearchParams();
  const cicloDaUrl = Number(busca.get('ciclo')) || null;
  const [ciclos, setCiclos] = useState<CicloResumo[]>([]);
  const [podeGerirLista, setPodeGerirLista] = useState<boolean | null>(null);
  const [cicloId, setCicloId] = useState<number | null>(null);
  const [dados, setDados] = useState<CicloCompleto | null>(null);
  const [contas, setContas] = useState<Conta[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [semAcesso, setSemAcesso] = useState(false);
  const [aba, setAba] = useState('semanas');

  const [importar, setImportar] = useState(false);
  const [modelo, setModelo] = useState<string>(MODELOS[0].value);
  const [inicio, setInicio] = useState<string | null>(segundaDaSemana(hoje));
  const [importando, setImportando] = useState(false);
  const [ligando, setLigando] = useState(false);
  const [previa, setPrevia] = useState<PreviaModelo | null>(null);
  const [previaAberta, setPreviaAberta] = useState(false);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [aplicando, setAplicando] = useState(false);

  const falha = useCallback((e: unknown) => {
    if (ehSemAcesso(e)) setSemAcesso(true); else setErro(erroDe(e));
  }, []);

  const carregarCiclo = useCallback(async (id: number) => {
    try { setDados(await obterCiclo(id)); setErro(null); } catch (e) { falha(e); }
  }, [falha]);

  const carregarLista = useCallback(async (escolher?: number) => {
    setCarregando(true);
    try {
      const r = await listarCiclos();
      setCiclos(r.ciclos);
      setPodeGerirLista(r.pode_gerir);
      const alvo = escolher ?? cicloId ?? (r.ciclos.find((c) => c.id === cicloDaUrl))?.id ?? (r.ciclos.find((c) => c.status === 'ligado') || r.ciclos[0])?.id ?? null;
      setCicloId(alvo);
      if (alvo) await carregarCiclo(alvo); else setDados(null);
    } catch (e) { falha(e); } finally { setCarregando(false); }
  }, [cicloId, cicloDaUrl, carregarCiclo, falha]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void carregarLista(); }, []);

  const pode_gerir = dados?.permissoes.pode_gerir ?? false;
  useEffect(() => {
    if (!pode_gerir) return;
    listarContas().then(setContas).catch((e) => message.error(`Não consegui carregar a lista de pessoas: ${erroDe(e)}`));
  }, [pode_gerir, message]);

  const salvar = useCallback(async (fn: () => Promise<unknown>, ok?: string): Promise<boolean> => {
    try {
      await fn();
      if (ok) message.success(ok);
      if (cicloId) await carregarCiclo(cicloId);
      return true;
    } catch (e) {
      if (ehConflito(e)) {
        message.warning('Outra pessoa mudou isto antes de você. Atualizei a tela; confira e faça de novo.');
        if (cicloId) await carregarCiclo(cicloId);
      } else {
        message.error(erroDe(e));
      }
      return false;
    }
  }, [cicloId, carregarCiclo, message]);

  const resumo = useMemo(() => (dados ? resumoDoCiclo(dados.etapas, hoje, dados.ciclo.status === 'ligado') : null), [dados, hoje]);
  const semana = useMemo(() => (dados ? semanaAtual(dados.etapas, hoje) : null), [dados, hoje]);

  const trocarCiclo = (id: number) => { setCicloId(id); setDados(null); void carregarCiclo(id); };

  const confirmarImportar = async () => {
    if (!inicio) { message.warning('Escolha a data de início.'); return; }
    setImportando(true);
    try {
      const r = await importarModelo(modelo, inicio);
      message.success('Modelo importado.');
      setImportar(false);
      await carregarLista(r?.ciclo_id ?? r?.ciclo?.id ?? undefined);
    } catch (e) { message.error(erroDe(e)); } finally { setImportando(false); }
  };

  const confirmarLigar = () => {
    if (!dados) return;
    modal.confirm({
      title: 'Ligar o ciclo?',
      content: 'Cada pessoa passa a atualizar a situação das próprias tarefas aqui no Plano de ação, e quem tem tarefa aberta recebe um aviso no sino. Nada é criado em Minhas atividades.',
      okText: 'Ligar o ciclo', cancelText: 'Agora não',
      onOk: async () => {
        setLigando(true);
        const ok = await salvar(() => ligarCiclo(dados.ciclo.id), 'Ciclo ligado. As pessoas foram avisadas pelo sino.');
        setLigando(false);
        if (ok) await carregarLista(dados.ciclo.id);
      },
    });
  };

  // "Atualizar do modelo": primeiro a prévia (nada muda), depois aplicar.
  const abrirPrevia = async () => {
    if (!dados) return;
    setCarregandoPrevia(true);
    try {
      setPrevia(await atualizarDoModelo(dados.ciclo.id, false));
      setPreviaAberta(true);
    } catch (e) { message.error(erroDe(e)); } finally { setCarregandoPrevia(false); }
  };
  const aplicarModelo = async () => {
    if (!dados) return;
    setAplicando(true);
    try {
      await atualizarDoModelo(dados.ciclo.id, true);
      message.success('Plano atualizado pelo modelo.');
      setPreviaAberta(false);
      await carregarLista(dados.ciclo.id);
    } catch (e) { message.error(erroDe(e)); } finally { setAplicando(false); }
  };

  if (semAcesso) return <div style={{ padding: 24 }}><Alert type="warning" showIcon message="Você não tem acesso ao Plano de ação" /></div>;

  const ctx: Contexto | null = dados ? { dados, hoje, salvar, contas } : null;
  const gestor = dados ? dados.permissoes.pode_gerir : podeGerirLista === true;

  return (
    <div style={{ padding: 16, display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
        <Title level={3} style={{ margin: 0 }}>Plano de ação</Title>
        <Space wrap>
          {ciclos.length > 0 && (
            <Select
              style={{ minWidth: 280 }} showSearch optionFilterProp="label" value={cicloId ?? undefined} placeholder="Escolha o ciclo"
              options={ciclos.map((c) => ({ value: c.id, label: c.titulo }))} onChange={trocarCiclo}
            />
          )}
          {gestor && <Button onClick={() => setImportar(true)}>Importar modelo</Button>}
          {gestor && dados && dados.ciclo.status !== 'encerrado' && <Button loading={carregandoPrevia} onClick={() => void abrirPrevia()}>Atualizar do modelo</Button>}
          {gestor && dados?.ciclo.status === 'rascunho' && <Button type="primary" loading={ligando} onClick={confirmarLigar}>Ligar o ciclo</Button>}
        </Space>
      </div>

      {erro && <Alert type="error" showIcon message="Não foi possível carregar" description={erro} action={<Button size="small" onClick={() => void carregarLista()}>Tentar de novo</Button>} />}

      {carregando && !dados && <Skeleton active />}

      {!carregando && !erro && !dados && (
        <Empty description={ciclos.length ? 'Escolha um ciclo.' : 'Ainda não há nenhum ciclo no Plano de ação.'}>
          {!ciclos.length && gestor && <Button type="primary" onClick={() => setImportar(true)}>Importar modelo</Button>}
        </Empty>
      )}

      {dados && ctx && resumo && semana && (
        <>
          <Card size="small">
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', alignItems: 'center' }}>
              <div>
                <Title level={4} style={{ margin: 0 }}>{dados.ciclo.titulo}</Title>
                <Text type="secondary">
                  {dados.ciclo.consultoria ? `${dados.ciclo.consultoria} · ` : ''}{fmtDataBr(dados.ciclo.inicio)} a {fmtDataBr(dados.ciclo.fim)}
                  {dados.ciclo.status === 'rascunho' ? ' · ainda não ligado' : ''}
                </Text>
                {dados.ciclo.pergunta && <div style={{ marginTop: 6, fontStyle: 'italic' }}>{dados.ciclo.pergunta}</div>}
              </div>
              <div>
                <Text strong style={{ fontSize: 18 }}>{fraseDaSemana(semana)}</Text>
                <Progress percent={resumo.pct} />
                <Text type="secondary" style={{ fontSize: 12 }}>{resumo.feitas} de {resumo.total} tarefas concluídas</Text>
              </div>
              <div style={{ display: 'flex', gap: 24 }}>
                <div>
                  <div style={{ fontSize: 28, lineHeight: 1.1, color: resumo.atrasadas ? '#cf1322' : undefined }}>{resumo.atrasadas}</div>
                  <Text type="secondary">{resumo.atrasadas === 1 ? 'tarefa atrasada' : 'tarefas atrasadas'}</Text>
                </div>
                <div>
                  <div style={{ fontSize: 28, lineHeight: 1.1, color: resumo.travadas ? '#cf1322' : undefined }}>{resumo.travadas}</div>
                  <Text type="secondary">{resumo.travadas === 1 ? 'semana travada' : 'semanas travadas'}</Text>
                </div>
              </div>
            </div>
          </Card>

          <Tabs
            activeKey={aba} onChange={setAba}
            items={[
              { key: 'semanas', label: 'Semanas', children: <AbaSemanas ctx={ctx} /> },
              { key: 'indicadores', label: 'Indicadores', children: <AbaIndicadores ctx={ctx} /> },
              { key: 'antes', label: 'Antes de começar e marcos', children: <AbaAntesEMarcos ctx={ctx} /> },
              { key: 'pessoas', label: 'Pessoas', children: <AbaPessoas ctx={ctx} /> },
            ]}
          />
        </>
      )}

      <Modal
        open={previaAberta} title="Atualizar do modelo: o que vai mudar" onCancel={() => setPreviaAberta(false)} onOk={aplicarModelo}
        okText="Aplicar" cancelText="Fechar" confirmLoading={aplicando} okButtonProps={{ disabled: !previa?.mudancas.length }}
        width="94vw" style={{ maxWidth: 1200, top: 16 }} destroyOnHidden
      >
        <Text type="secondary">Nada é apagado, e o que alguém já editou à mão fica como está.</Text>
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', marginTop: 12 }}>
          <div>
            <Text strong>Vai mudar ({previa?.mudancas.length ?? 0})</Text>
            {previa?.mudancas.length ? <ul style={{ paddingLeft: 18 }}>{previa.mudancas.map((m, i) => <li key={i}>{textoDoItem(m)}</li>)}</ul> : <div><Text type="secondary">Nada a mudar: o plano já está igual ao modelo.</Text></div>}
          </div>
          <div>
            <Text strong>Fica como está ({previa?.ignoradas.length ?? 0})</Text>
            {previa?.ignoradas.length ? <ul style={{ paddingLeft: 18 }}>{previa.ignoradas.map((m, i) => <li key={i}>{textoDoItem(m)}</li>)}</ul> : <div><Text type="secondary">Nenhum item deixado de fora.</Text></div>}
          </div>
        </div>
      </Modal>

      <Modal
        open={importar} title="Importar modelo" onCancel={() => setImportar(false)} onOk={confirmarImportar} okText="Importar"
        cancelText="Cancelar" confirmLoading={importando} width="94vw" style={{ maxWidth: 560, top: 16 }} destroyOnHidden
      >
        <Form layout="vertical">
          <Form.Item label="Modelo">
            <Select showSearch optionFilterProp="label" options={MODELOS} value={modelo} onChange={setModelo} />
          </Form.Item>
          <Form.Item label="Primeira segunda-feira do ciclo" extra="As datas das semanas e das tarefas contam a partir deste dia.">
            <CampoData valor={inicio} onChange={setInicio} />
          </Form.Item>
          <Text type="secondary">Importar só cria o plano. As pessoas só são avisadas quando você ligar o ciclo.</Text>
        </Form>
      </Modal>
    </div>
  );
}
